import React from 'react';
import { Users, Mail, LucideIcon } from 'lucide-react';
import DecisionMakerPreviewStack, { DecisionMakerPreviewPerson } from './DecisionMakerPreviewStack';

export interface CompanySelectorPlace {
  place_id: number;
  place_name: string;
  item_count: number;
  people?: DecisionMakerPreviewPerson[];
}

interface CompanySelectorProps {
  place: CompanySelectorPlace;
  selected: boolean;
  onToggle: () => void;
  icon?: 'people' | 'mail';
  iconClassName?: string;
  itemNoun?: string;
  unitLabel?: string;
  showAvatar?: boolean;
}

const ICONS: Record<string, LucideIcon> = {
  people: Users,
  mail: Mail,
};

const COMPANY_AVATAR_HUES = ['#059669', '#2563EB', '#7C3AED', '#D97706', '#DC2626', '#0891B2', '#BE185D'];

function getCompanyInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function getCompanyAvatarHue(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return COMPANY_AVATAR_HUES[hash % COMPANY_AVATAR_HUES.length];
}

const DEFAULT_ICON_CLASS: Record<string, string> = {
  people: 'text-purple-600',
  mail: 'text-gray-500',
};

const CompanySelector: React.FC<CompanySelectorProps> = ({
  place,
  selected,
  onToggle,
  icon = 'people',
  iconClassName,
  itemNoun = 'contact',
  unitLabel = 'people enrichment',
  showAvatar = false,
}) => {
  const Icon = ICONS[icon];
  const resolvedIconClass = iconClassName ?? DEFAULT_ICON_CLASS[icon] ?? 'text-gray-500';
  const noun = place.item_count !== 1 ? `${itemNoun}s` : itemNoun;
  const hasPeoplePreview = Boolean(place.people?.length);

  return (
    <div
      className={`cursor-pointer rounded-lg border px-4 py-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ${
        selected
          ? 'border-primary bg-[#fdf5f5]'
          : 'border-gray-200 bg-white hover:border-primary/60 hover:bg-gray-50'
      }`}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            onClick={(e) => e.stopPropagation()}
            className="h-5 w-5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
          />
          {showAvatar && (
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ background: getCompanyAvatarHue(place.place_name) }}
              aria-hidden
            >
              {getCompanyInitials(place.place_name)}
            </div>
          )}
          <span className="truncate text-base font-medium text-gray-900">{place.place_name}</span>
        </div>
        <span
          className={`ml-2 inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-sm font-medium ${
            selected ? 'bg-white text-primary ring-1 ring-primary/10' : 'bg-gray-100 text-gray-700'
          }`}
        >
          <Icon className={`h-4 w-4 ${resolvedIconClass}`} />
          {place.item_count}
        </span>
      </div>
      {hasPeoplePreview ? (
        <div className={showAvatar ? 'ml-[4.25rem]' : 'ml-8'}>
          <DecisionMakerPreviewStack
            people={place.people ?? []}
            forceGray
            roleMaxWidthClassName="max-w-[220px] sm:max-w-[300px]"
          />
        </div>
      ) : (
        <div className={`text-sm leading-snug text-gray-600 ${showAvatar ? 'ml-[4.25rem]' : 'ml-8'}`}>
          {place.item_count} {noun} &times; 1 {unitLabel} each ={' '}
          {place.item_count} credit{place.item_count !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default CompanySelector;
